import React from "react";
import CustomTable from "../components/common/table/CustomTable";
import {
  fetchRoutingPlanDetail,
  RoutingPlanDetailsType,
  useRoutingPlanDetailQuery,
} from "../services/RoutingPlanDetailsService";
import {
  Instruction,
  InstructionType,
  useInstructionsQuery,
} from "../services/InstructionsService";
import {
  getProviderOptions,
} from "../services/ProviderService";
import {
  ConditionValueType as FallBackConditionValueType,
  fetchFallBackRouting,
} from "../services/FallBackRoutingService";
import {
  fetchSenderTranslation,
  SenderTranslationType,
  ConditionValueType as SenderConditionValueType,
} from "../services/SenderTranslationService";
import {
  ContentTranslationType,
  fetchContentTranslation,
} from "../services/ContentTranslationService";
import {
  ConditionValueType as SubRoutingConditionValueType,
  fetchSubRouting,
} from "../services/SubRoutingService";
import { fetchTpsRouting } from "../services/TPSRoutingService";
import { getOperatorNameOptions } from "../services/OperatorsService";
import { getRoutingPlanName } from "../services/RoutingPlanService";

const instructionSummaryCache = new Map<string, Promise<string>>();

const DLR_STATUS_LABELS: Record<string, string> = {
  ENROUTE: "ENROUTE",
  DELIVRD: "DELIVERED",
  EXPIRED: "EXPIRED",
  DELETED: "DELETED",
  UNDELIV: "UNDELIVERABLE",
  ACCEPTD: "ACCEPTED",
  UNKNOWN: "UNKNOWN",
  REJECTD: "REJECTED",
};

const SUB_ROUTING_FILTER_LABELS: Record<string, string> = {
  SENDER: "Sender ID",
  CONTENT: "Content",
  RECEIVER: "Receiver",
  IP: "IP",
};

const getDlrStatusLabel = (status?: string | null) => {
  if (!status) {
    return "Unknown status";
  }

  return DLR_STATUS_LABELS[status] || status;
};

const formatFallBackCondition = (condition: FallBackConditionValueType) => {
  const statusLabel = getDlrStatusLabel(condition.status);

  if (condition.conditionType === "EQUAL") {
    return `DLR status is ${statusLabel}`;
  }

  if (condition.conditionType === "NOT_EQUAL") {
    return `DLR status is not ${statusLabel}`;
  }

  if (condition.conditionType && condition.status) {
    return `${condition.conditionType}: ${statusLabel}`;
  }

  return condition.conditionType || statusLabel;
};

const formatFallBackSummary = (conditions?: FallBackConditionValueType[]) => {
  if (!conditions || conditions.length === 0) {
    return "No Conditions";
  }

  return conditions.map(formatFallBackCondition).join(", ");
};

const formatSenderCondition = (condition: SenderConditionValueType) => {
  const value = condition.value?.trim();

  switch (condition.conditionType) {
    case "CONTAINS":
      return value ? `Sender ID contains ${value}` : "Sender ID contains";
    case "EQUAL_TO":
      return value ? `Sender ID equals ${value}` : "Sender ID equals";
    case "IS_DIGIT":
      return "Sender ID is digit";
    case "IS_NOT_DIGIT":
      return "Sender ID is not digit";
    default:
      if (condition.conditionType && value) {
        return `Sender ID ${condition.conditionType} ${value}`;
      }

      if (condition.conditionType) {
        return `Sender ID ${condition.conditionType}`;
      }

      return value ? `Sender ID ${value}` : "Sender ID condition";
  }
};

const formatSenderSummary = (senderTranslation: SenderTranslationType) => {
  const conditions = senderTranslation.conditionValues;

  if (!conditions || conditions.length === 0) {
    return "No Conditions";
  }

  return conditions.reduce((summary, condition, index) => {
    const formattedCondition = formatSenderCondition(condition);

    if (index === 0) {
      return formattedCondition;
    }

    const logicalOperator =
      condition.logicalOperator || senderTranslation.logicalOperator || "AND";
    return `${summary} ${logicalOperator} ${formattedCondition}`;
  }, "");
};

const formatContentSummary = (translation: ContentTranslationType) => {
  const contentContains =
    translation.contentContains || translation.contentExtract || "";

  if (translation.operationType === "REPLACE") {
    return `If content contains (${contentContains}) replace (${translation.replaceValue || ""}) with (${translation.replaceWithValue || ""})`;
  }

  if (translation.operationType === "FORMAT_DIGITS") {
    return "If content is digits ONLY";
  }

  return `Extract code if content contains (${contentContains})`;
};

const formatSubRoutingCondition = (condition: SubRoutingConditionValueType) => {
  const filterLabel =
    SUB_ROUTING_FILTER_LABELS[condition.filterType] || condition.filterType || "Condition";
  const value = condition.value?.trim();

  switch (condition.conditionType) {
    case "EQUALS":
      return value ? `${filterLabel} equals ${value}` : `${filterLabel} equals`;
    case "CONTAINS":
      return value ? `${filterLabel} contains ${value}` : `${filterLabel} contains`;
    case "STARTS_WITH":
      return value
        ? `${filterLabel} starts with ${value}`
        : `${filterLabel} starts with`;
    case "ALFA":
      return `${filterLabel} is alfa`;
    case "NUMERIC":
      return `${filterLabel} is numeric`;
    case "ALPHANUMERIC":
      return `${filterLabel} is alphanumeric`;
    case "URL":
      return `${filterLabel} is URL`;
    case "LIST":
      return value ? `${filterLabel} matches list ${value}` : `${filterLabel} matches list`;
    default:
      if (condition.conditionType && value) {
        return `${filterLabel} ${condition.conditionType} ${value}`;
      }

      return filterLabel;
  }
};

const formatSubRoutingSummary = (conditions?: SubRoutingConditionValueType[]) => {
  if (!conditions || conditions.length === 0) {
    return "No Conditions";
  }

  return conditions.map(formatSubRoutingCondition).join(", ");
};

const formatRatedRoutingSummary = async (
  routingDetails: RoutingPlanDetailsType
) => {
  if (routingDetails.rate === undefined || routingDetails.rate === null) {
    return "Rate: -";
  }

  return `Rate: ${routingDetails.rate}`;
};

const getInstructionSummary = async (instruction: Instruction) => {
  const cacheKey = `${instruction.type}:${instruction.id}`;
  const cachedSummary = instructionSummaryCache.get(cacheKey);

  if (cachedSummary) {
    return cachedSummary;
  }

  const summaryPromise = (async () => {
    switch (instruction.type) {
      case InstructionType.FALL_BACK_ROUTING: {
        const detail = await fetchFallBackRouting(String(instruction.id));
        return formatFallBackSummary(detail.conditionStatuses);
      }
      case InstructionType.SENDER_TRANSLATION: {
        const detail = await fetchSenderTranslation(String(instruction.id));
        return formatSenderSummary(detail);
      }
      case InstructionType.CONTENT_TRANSLATION: {
        const detail = await fetchContentTranslation(instruction.id);
        return formatContentSummary(detail);
      }
      case InstructionType.SUB_ROUTING: {
        const detail = await fetchSubRouting(String(instruction.id));
        return formatSubRoutingSummary(detail.conditionValues);
      }
      case InstructionType.TPS_ROUTE: {
        const detail = await fetchTpsRouting(String(instruction.id));
        return `If TPS is above ${detail.tpsAbove}`;
      }
      case InstructionType.RATED_ROUTING: {
        const detail = await fetchRoutingPlanDetail(String(instruction.id));
        return formatRatedRoutingSummary(detail);
      }
      default:
        return instruction.type;
    }
  })().catch(() => instruction.type);

  instructionSummaryCache.set(cacheKey, summaryPromise);
  return summaryPromise;
};

interface ImpactedInstructionsProps {
  id: RoutingPlanDetailsType["id"];
  open: boolean;
}

export function ImpactedInstructions(
  props: Readonly<ImpactedInstructionsProps>
) {
  const { data: row, isPending, isError } = useRoutingPlanDetailQuery(props.id);
  if (isPending) {
    return <div>Pending</div>;
  }
  if (isError || !row) {
    return <div>Error</div>;
  }
  return <ImpactedInstructionsFetched row={row} />;
}

interface ImpactedInstructionsFetchedProps {
  row: RoutingPlanDetailsType;
}

function ImpactedInstructionsFetched(
  props: Readonly<ImpactedInstructionsFetchedProps>
) {
  return (
    <CustomTable fullBleedMobile={false}
      filters={{
        operatorId: props.row.operatorId,
        routingPlanId: Array.isArray(props.row.routingPlanId) ? props.row.routingPlanId[0] : props.row.routingPlanId,
      }}
      fetchFunc={useInstructionsQuery}
      actions={[]}
      fields={[
        { key: "type", header: "Type" },
        { key: "routingPlanId", header: "Routing Plan", options: (t) => getRoutingPlanName(t.routingPlanId) },
        { key: "operatorId", header: "Operator", options: (t) => getOperatorNameOptions(t.operatorId) },
        { key: "providerId", header: "Provider", options: (t) => getProviderOptions(t.providerId) },
        {
          key: "type",
          header: "Summary",
          transform: async (t) => getInstructionSummary(t),
        },
        { key: "effectiveDate", header: "Effective Date" },
      ]}
    />
  );
}

export default ImpactedInstructions;